// AI Citator (case treatment badge + panel) analytics enumerations.

import { CaseStatusEnum, type DisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { CaseReviewEnum, type DisplayableCaseReview } from '@/citator/schema/case-review-schema'

export enum CitatorBadgeType {
  NoFlagsDetected = 'no_flags_detected',
  UseCaution = 'use_caution',
  OverrulingRisk = 'overruling_risk',
  NegativeTreatment = 'negative_treatment',
}

// Distinguishes the disclosure rung, not the screen: plain values are treatment UI
// the user reveals by interacting (hover card, references list); `*_inline` values
// are the always-visible flag glyph beside the citation in prose (impression =
// flag rendered, click = flag clicked).
export enum CitatorSurface {
  Chat = 'chat',
  ChatInline = 'chat_inline',
  Drafting = 'drafting',
  DraftingInline = 'drafting_inline',
  OpinionViewerInline = 'opinion_viewer_inline',
}

export type CitatorSurfaceInline = CitatorSurface.ChatInline | CitatorSurface.DraftingInline | CitatorSurface.OpinionViewerInline

export enum CitatorPanelSource {
  BadgeClick = 'badge_click',
  CitationClick = 'citation_click',
}

// Which component presented the citator detail: the in-page content viewer, or the
// standalone reference viewer (today always a new browser tab, e.g. from the canvas
// or a draft workspace too narrow for the chat panel). Named for the component, not
// the window mechanics.
export enum CitatorOpenedIn {
  ContentViewer = 'content_viewer',
  StandaloneViewer = 'standalone_viewer',
}

// Mirrors backend `CaseReviewEnum` 1:1; the emitted value is the backend key (not
// the UI label) to keep a stable analytics join key, decoupled from display copy.
export enum CitatorTreatmentOutcome {
  Overturned = 'overturned',
  OverturnedInPart = 'overturned_in_part',
  Vacated = 'vacated',
  OverturningRecognized = 'overturning_recognized',
  Refined = 'refined',
  Distinguished = 'distinguished',
  Questioned = 'questioned',
  Undermined = 'undermined',
}

export function mapCaseStatusToBadgeType(status: DisplayableCaseStatus): CitatorBadgeType {
  switch (status) {
    case CaseStatusEnum.likely_good:
      return CitatorBadgeType.NoFlagsDetected
    case CaseStatusEnum.likely_bad:
      return CitatorBadgeType.NegativeTreatment
    case CaseStatusEnum.overruling_risk:
      return CitatorBadgeType.OverrulingRisk
    case CaseStatusEnum.caution:
      return CitatorBadgeType.UseCaution
  }
}

export function mapCaseReviewToTreatmentOutcome(review: DisplayableCaseReview): CitatorTreatmentOutcome {
  switch (review) {
    case CaseReviewEnum.overturned:
      return CitatorTreatmentOutcome.Overturned
    case CaseReviewEnum.overturned_in_part:
      return CitatorTreatmentOutcome.OverturnedInPart
    case CaseReviewEnum.vacated:
      return CitatorTreatmentOutcome.Vacated
    case CaseReviewEnum.overturning_recognized:
      return CitatorTreatmentOutcome.OverturningRecognized
    case CaseReviewEnum.refined:
      return CitatorTreatmentOutcome.Refined
    case CaseReviewEnum.distinguished:
      return CitatorTreatmentOutcome.Distinguished
    case CaseReviewEnum.questioned:
      return CitatorTreatmentOutcome.Questioned
    case CaseReviewEnum.undermined:
      return CitatorTreatmentOutcome.Undermined
  }
}
